import { FormEvent, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import type { ContactFormProps } from '@/types/theme';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const fieldLabel = 'text-[10px] font-semibold uppercase tracking-[0.42em] text-foreground/38';
const fieldInput = [
	'w-full border-b border-border/55 bg-transparent py-2.5',
	'text-[14.5px] text-foreground placeholder:text-foreground/18',
	'transition-colors duration-200 focus:border-accent/60 focus:outline-none',
].join(' ');
const fieldTextarea = [
	'w-full resize-none border-b border-border/55 bg-transparent py-2.5',
	'text-[14.5px] text-foreground placeholder:text-foreground/18',
	'transition-colors duration-200 focus:border-accent/60 focus:outline-none',
].join(' ');

export function ContactForm({
	eyebrow,
	title,
	phoneNumber,
	description,
	nameLabel,
	emailLabel,
	subjectLabel,
	messageLabel,
	submitLabel,
	successMessage,
	restUrl,
	nonce,
	editorPreview,
}: ContactFormProps) {
	const [status, setStatus] = useState<FormStatus>('idle');
	const [errorMessage, setErrorMessage] = useState('');
	const eyebrowText = eyebrow || 'Contattaci';
	const phoneHeading = phoneNumber || title || '+39';
	const labels = {
		name:    nameLabel    || 'Name',
		email:   emailLabel   || 'Email',
		subject: subjectLabel || 'Subject',
		message: messageLabel || 'Message',
	};

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();

		if (!restUrl || !nonce) {
			setStatus('error');
			setErrorMessage('The form endpoint is not configured.');
			return;
		}

		const formData = new FormData(event.currentTarget);
		const payload = {
			name:    String(formData.get('name')    || ''),
			email:   String(formData.get('email')   || ''),
			subject: String(formData.get('subject') || ''),
			message: String(formData.get('message') || ''),
			company: String(formData.get('company') || ''),
			nonce,
		};

		setStatus('submitting');
		setErrorMessage('');

		const response = await fetch(restUrl, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
		});

		const responseBody = (await response.json().catch(() => ({}))) as { message?: string };

		if (!response.ok) {
			setStatus('error');
			setErrorMessage(responseBody.message || 'The form could not be sent.');
			return;
		}

		event.currentTarget.reset();
		setStatus('success');
	}

	/* ── Left editorial panel (shared between preview and live) ── */
	const infoPanel = (
		<div className="flex flex-col justify-between gap-12">
			<div>
				{/* Eyebrow */}
				<div className="flex items-center gap-3">
					<div className="h-px w-6 shrink-0 bg-accent/60" aria-hidden="true" />
					<p className="text-[10px] font-semibold uppercase tracking-[0.44em] text-accent/75">
						{eyebrowText}
					</p>
				</div>

				{/* Phone / headline */}
				<h2 className="mt-6 font-serif text-5xl leading-[0.92] tracking-tight text-foreground md:text-6xl">
					{phoneHeading}
				</h2>

				{/* Description */}
				{description ? (
					<div className="mt-7 flex items-start gap-4">
						<div className="mt-2 h-px w-8 shrink-0 bg-foreground/15" aria-hidden="true" />
						<p className="text-[14px] leading-[1.9] text-foreground/42">{description}</p>
					</div>
				) : null}
			</div>

			{/* Closing decoration */}
			<div className="flex items-center gap-3">
				<div className="h-px flex-1 bg-gradient-to-r from-border/60 to-transparent" aria-hidden="true" />
				<div className="h-0.5 w-0.5 rounded-full bg-accent/55 shadow-[0_0_5px_rgba(184,154,50,0.5)]" aria-hidden="true" />
				<div className="h-px w-8 bg-border/35" aria-hidden="true" />
			</div>
		</div>
	);

	/* ── Editor preview ──────────────────────────────────────── */
	if (editorPreview) {
		return (
			<section className="relative overflow-hidden">
				<div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
					{infoPanel}

					<div className="space-y-5 border-t border-border/40 pt-10 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0">
						<p className="text-[9.5px] font-semibold uppercase tracking-[0.44em] text-foreground/30">
							Form fields
						</p>
						{[labels.name, labels.email, labels.subject, labels.message].map((l) => (
							<div key={l} className="border-b border-border/45 pb-2.5">
								<span className={fieldLabel}>{l}</span>
							</div>
						))}
						<p className="pt-2 text-[12.5px] leading-relaxed text-foreground/30">
							Submissions are routed through the WordPress REST endpoint on the frontend.
						</p>
					</div>
				</div>
			</section>
		);
	}

	/* ── Live form ───────────────────────────────────────────── */
	return (
		<section className="relative overflow-hidden">
			{/* Atmospheric right-side glow */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute right-0 top-0 h-[380px] w-[380px] translate-x-1/3 -translate-y-1/4 rounded-full bg-accent/[0.05] blur-[120px]"
			/>

			<div className="relative grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
				{infoPanel}

				{/* Form */}
				<form
					className="flex flex-col gap-7 border-t border-border/40 pt-10 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0"
					onSubmit={handleSubmit}
					noValidate
				>
					{/* Name + Email row */}
					<div className="grid gap-7 sm:grid-cols-2">
						<div className="flex flex-col gap-2.5">
							<label className={fieldLabel}>{labels.name}</label>
							<input className={fieldInput} type="text" name="name" required placeholder=" " />
						</div>
						<div className="flex flex-col gap-2.5">
							<label className={fieldLabel}>{labels.email}</label>
							<input className={fieldInput} type="email" name="email" required placeholder=" " />
						</div>
					</div>

					{/* Subject */}
					<div className="flex flex-col gap-2.5">
						<label className={fieldLabel}>{labels.subject}</label>
						<input className={fieldInput} type="text" name="subject" required placeholder=" " />
					</div>

					{/* Honeypot */}
					<label className="hidden" aria-hidden="true">
						<span>Company</span>
						<input type="text" name="company" autoComplete="off" tabIndex={-1} />
					</label>

					{/* Message */}
					<div className="flex flex-col gap-2.5">
						<label className={fieldLabel}>{labels.message}</label>
						<textarea className={`${fieldTextarea} min-h-[8rem]`} name="message" required placeholder=" " />
					</div>

					{/* Submit + feedback */}
					<div className="flex flex-col gap-3 pt-1">
						<button
							type="submit"
							disabled={status === 'submitting'}
							className={[
								'group relative inline-flex items-center justify-center gap-2.5 overflow-hidden rounded-[0.65rem]',
								'bg-accent px-6 py-3 text-[13px] font-semibold tracking-[0.01em] text-accent-foreground',
								'shadow-[0_1px_0_rgba(255,255,255,0.13)_inset,0_2px_14px_rgba(184,154,50,0.35)]',
								'transition-all duration-300 hover:brightness-105 hover:shadow-[0_1px_0_rgba(255,255,255,0.13)_inset,0_4px_22px_rgba(184,154,50,0.50)]',
								'disabled:cursor-not-allowed disabled:opacity-55',
							].join(' ')}
						>
							<span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
							{status === 'submitting' ? 'Sending…' : (submitLabel || 'Send message')}
							{status !== 'submitting' && (
								<ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
							)}
						</button>

						{status === 'success' && (
							<div className="flex items-center gap-2.5">
								<div className="h-1 w-1 rounded-full bg-accent/80 shadow-[0_0_4px_rgba(184,154,50,0.6)]" aria-hidden="true" />
								<p className="text-[13px] text-accent/80">
									{successMessage || 'Your message has been sent successfully.'}
								</p>
							</div>
						)}
						{status === 'error' && (
							<p className="text-[13px] text-red-600">{errorMessage}</p>
						)}
					</div>
				</form>
			</div>
		</section>
	);
}
